<script setup lang="ts">
import { CommentFilters, getNumberOfComments } from "@/services/comments";

import { useApplicationStore } from "@/store/application";
import { getNumberOfPosts } from "@/services/posts";
import { getNumberOfSocialMediaUsers } from "@/services/user";

import tailwindConfig from "../../tailwind.config";

const themeColors = tailwindConfig.theme.extend.colors;
const iconColor = themeColors["ppa-dark-blue"];

export type NavBarFeature = {
  key: string;
  label: string;
  url: string;
  folders: {
    label: string;
    key: string;
    url: string;
    filters: CommentFilters;
  }[];
  watchlists: {
    label: string;
    key: string;
    isPublic: boolean;
    url: string;
    filters: CommentFilters;
  }[];
};

const props = defineProps<{
  feature: NavBarFeature;
  mainColor: string;
  activeFeatureKey: string | null;
  isCurrentPlatformActive: boolean;
  isPlatformOpen: boolean;
  platformKey: string;
  accentColor: string;
  isNavbarExpanded: boolean;
}>();

defineOptions({
  inheritAttrs: false,
});

const applicationStore = useApplicationStore();

const socialMediaPlatformParameter = computed(() =>
  applicationStore.socialMediaPlatformParameter(props.platformKey),
);

const folderCounts = ref<Record<string, number>>();
const watchlistCounts = ref<Record<string, number>>();

const fetchFolderCounts = async (feature: string) => {
  if (!socialMediaPlatformParameter.value) {
    return;
  }
  const { socialMediaTypeId, socialMediaConnectionId } = socialMediaPlatformParameter.value;
  if (feature === "comments") {
    const commentCounts = await getNumberOfComments(socialMediaTypeId, socialMediaConnectionId);
    folderCounts.value = commentCounts.folders;
    watchlistCounts.value = commentCounts.watchlists;
  } else if (feature === "posts") {
    const postlistCounts = await getNumberOfPosts(socialMediaTypeId, socialMediaConnectionId);
    folderCounts.value = postlistCounts.folders;
  } else if (feature === "userlist") {
    const socialMediaUserCounts = await getNumberOfSocialMediaUsers(
      socialMediaTypeId,
      socialMediaConnectionId,
    );
    folderCounts.value = socialMediaUserCounts.folders;
    watchlistCounts.value = socialMediaUserCounts.watchlists;
  }
};

const folders = computed(() => [
  ...props.feature.folders.map((f) => ({
    ...f,
    isWatchlist: false,
    isPublic: true,
    numberOfItems: folderCounts.value ? folderCounts.value[f.key] : 0,
  })),
  ...props.feature.watchlists.map((wl) => ({
    ...wl,
    isWatchlist: true,
    numberOfItems: watchlistCounts.value ? watchlistCounts.value[wl.key.split("-")[1] ?? ""] : 0,
  })),
]);

const isFeatureOpen = ref(false);

const router = useRouter();

const featureSelected = (event: Event) => {
  if (props.feature.folders.length === 0) return router.push(props.feature.url);
  isFeatureOpen.value = !isFeatureOpen.value;
};

watch(
  isFeatureOpen,
  async (newValue) => {
    if (!newValue) {
      return;
    }
    const featureKey = props.feature.key;
    await fetchFolderCounts(featureKey);
  },
  { immediate: true },
);

watch(
  () => props.isPlatformOpen,
  (newValue) => {
    if (newValue) {
      isFeatureOpen.value =
        props.isCurrentPlatformActive && props.activeFeatureKey === props.feature.key;
    } else isFeatureOpen.value = props.activeFeatureKey === props.feature.key;
  },
  { immediate: true },
);
</script>

<template>
  <button
    class="navbar-platform-feature mb-2 flex min-h-[40px] items-center justify-between border-2 border-solid p-2 text-[15px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
    :class="{
      'group flex w-full cursor-pointer justify-between': feature.folders,
      'selected rounded-md border':
        feature.key === activeFeatureKey && feature.folders.length === 0,
    }"
    :aria-current="feature.key === props.activeFeatureKey ? 'page' : undefined"
    :aria-expanded="isFeatureOpen"
    @click="featureSelected"
    :aria-controls="`feature-group-{{ feature.key }}`"
    :aria-label="feature.label"
    v-bind="$attrs"
  >
    <NavBarLabel
      :color="mainColor"
      :platform="platformKey"
      :label="feature.label"
      :active="feature.key === activeFeatureKey"
      :iconName="feature.key"
      :feature="true"
      :isNavbarExpanded="isNavbarExpanded"
    />
    <div v-if="feature.folders.length !== 0" role="none" aria-hidden="true">
      <img
        v-if="feature.key === activeFeatureKey"
        :src="isFeatureOpen ? '/icons/active/arrow_down.svg' : '/icons/active/arrow_right.svg'"
        alt="arrow-down-icon"
        class="invisible group-hover:visible"
        aria-hidden="true"
      />
      <img
        v-else
        :src="isFeatureOpen ? '/icons/inactive/arrow_down.svg' : '/icons/inactive/arrow_right.svg'"
        alt="arrow-right-icon"
        class="invisible group-hover:visible"
        aria-hidden="true"
      />
    </div>
  </button>
  <div
    v-show="isFeatureOpen && feature.folders.length > 0"
    class="mb-2 ml-1 w-full pl-3 pt-3"
    role="group"
  >
    <NavBarPlatformFolder
      v-for="(folder, index) in folders"
      :folder="folder"
      :active="feature.key === activeFeatureKey || isFeatureOpen"
      :key="folder.key"
      :platformKey="platformKey"
      :aria-setsize="folders.length"
      :aria-posinset="index + 1"
      :numberOfItems="folder.numberOfItems"
      :isNavbarExpanded="isNavbarExpanded"
    >
      <template v-if="folder.isWatchlist">
        <IconFolderPublic v-if="folder.isPublic" class="flex-shrink-0" />
        <IconFolderPrivate v-else class="flex-shrink-0" />
      </template>
      <template v-else>
        <template v-if="feature.key === 'comments'">
          <IconCommentOpen v-if="folder.key === 'open'" :color="iconColor" />
          <IconCommentProgress v-else-if="folder.key === 'progress'" :color="iconColor" />
          <IconCommentDone v-else-if="folder.key === 'done'" :color="iconColor" />
        </template>
        <template v-else-if="feature.key === 'dms'">
          <IconMail v-if="folder.key === 'dms'" :color="iconColor" />
        </template>
        <template v-else-if="feature.key === 'posts'">
          <IconPostOpen v-if="folder.key === 'open'" :color="iconColor" />
          <IconCommentProgress v-if="folder.key === 'progress'" :color="iconColor" />
          <IconPostDone v-else-if="folder.key === 'done'" :color="iconColor" />
          <IconPost v-else-if="folder.key === 'all'" :color="iconColor" />
        </template>
        <template v-else-if="feature.key === 'userlist'">
          <IconUserActive v-if="folder.key === 'active'" :color="iconColor" />
          <IconUserBlocked v-else-if="folder.key === 'blocked'" :color="iconColor" />
          <IconUser v-else-if="folder.key === 'all'" :color="iconColor" />
        </template>
      </template>
    </NavBarPlatformFolder>
  </div>
</template>
<style>
.router-link {
  border-color: transparent;
}

.navbar-platform-feature {
  border-color: transparent;
}

.router-link-active,
.selected {
  --navbar-border-color: v-bind(mainColor);
  font-weight: 800;
  color: #042b5f;
  background-color: v-bind(accentColor);
  border-color: var(--navbar-border-color);
}
</style>
